import { sb } from '../supabase';

// Signed-URL helpers for request attachments on the ADMIN consoles.
//
// Request attachments live in the PRIVATE request-attachments bucket, so an
// admin list cannot put an object path straight into an <img src>. It has to
// mint a short-lived signed URL first, which works because of the admin-only
// SELECT policy added in migration 20260801000008 ("request-attachments admin
// read", bucket_id = 'request-attachments' and public.is_admin_user()).
//
// ⚠️ ADMIN SURFACES ONLY. The other two SELECT policies on that bucket are
// owner-by-folder (the uploading buyer) and nothing else — a SUPPLIER session
// matches neither, so calling this from a supplier-facing screen returns {} and
// renders a row of grey placeholders. /requests deliberately stays on the public
// reference_image column for that reason.
//
// These replaced requests.reference_image, a PUBLIC product-images URL that the
// request forms no longer write. Existing rows keep their value and every caller
// here falls back to it, so nothing that rendered before goes blank.

const IMAGE_RE = /\.(jpe?g|png|webp|gif)$/i;

/**
 * The first attachment that is actually an image, or null.
 *
 * A list thumbnail is an <img>, so a PDF or a ZIP must not be chosen for it —
 * it would render as a broken image rather than as no image. Attachments are a
 * mixed bag by design (the picker accepts images, PDF and ZIP), so "first path"
 * and "first image path" are genuinely different answers.
 */
export function firstImagePath(paths) {
  if (!Array.isArray(paths)) return null;
  return paths.find((p) => typeof p === 'string' && IMAGE_RE.test(p)) || null;
}

/**
 * Mint signed URLs for a batch of object paths → { path: signedUrl }.
 *
 * ONE createSignedUrls call per screen, not one per row: a 300-row quotation
 * board would otherwise make 300 round trips to render 300 thumbnails.
 *
 * Never throws and never rejects. A caller renders a placeholder for any path
 * that is missing from the result, which is the same thing it already renders
 * for a row with no image at all — so a signing failure degrades to "no
 * thumbnail", never to a broken screen. It is still logged with Supabase's own
 * message, because a silent {} here and a genuinely image-less list look
 * identical on screen and we would otherwise learn nothing about which it was.
 */
export async function signRequestAttachments(paths, ttlSeconds = 600) {
  const wanted = [...new Set((paths || []).filter((p) => typeof p === 'string' && p !== ''))];
  if (wanted.length === 0) return {};

  try {
    const { data, error } = await sb.storage
      .from('request-attachments')
      .createSignedUrls(wanted, ttlSeconds);

    if (error) {
      console.error('[requestAttachmentUrls] createSignedUrls failed:', error.message || error);
      return {};
    }

    const map = {};
    (Array.isArray(data) ? data : []).forEach((row) => {
      // Per-path errors ride INSIDE the array (row.error) rather than failing
      // the batch, so one deleted object must not blank the other thumbnails.
      if (row?.path && row?.signedUrl) map[row.path] = row.signedUrl;
      else if (row?.error) console.error('[requestAttachmentUrls] path not signed:', row.path, row.error);
    });
    return map;
  } catch (e) {
    console.error('[requestAttachmentUrls] createSignedUrls threw:', e?.message || e);
    return {};
  }
}

/**
 * Resolve ONE thumbnail per row, signed-first with a public fallback.
 *
 * Takes the rows and a function that returns each row's attachment paths, does a
 * single batched signing call, and returns a `resolve(row)` for the caller to
 * apply. The three cases, all of which exist in production right now:
 *
 *   old row     no attachment_paths, reference_image set  → the public URL
 *   dual-write  both set                                  → signed, public if signing failed
 *   new row     attachment_paths only                     → signed, else null (placeholder)
 */
export async function buildThumbResolver(rows, getPaths, ttlSeconds = 600) {
  const perRow = new Map();
  const all = [];

  (rows || []).forEach((r) => {
    const p = firstImagePath(getPaths(r));
    if (p) { perRow.set(r, p); all.push(p); }
  });

  const signed = await signRequestAttachments(all, ttlSeconds);

  return (row) => {
    const p = perRow.get(row);
    return (p && signed[p]) || row?.reference_image || null;
  };
}
